import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { FlightPathDivider } from "@/components/motifs";

const CATEGORIES = ["All", "Rings", "Necklaces", "Earrings", "Bracelets", "Anklets"];
const SEGMENTS = ["All", "Anti-Tarnish", "Oxidised"];
const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "newest", label: "Newest" },
  { key: "price_asc", label: "Price: Low to High" },
  { key: "price_desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");

  const category = params.get("category") || "All";
  const segment = params.get("segment") || "All";

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category, segment, search: search || undefined, sort } })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, segment, search, sort]);

  const setCategory = (c) => {
    if (c === "All") params.delete("category");
    else params.set("category", c);
    setParams(params);
  };

  const setSegment = (s) => {
    if (s === "All") params.delete("segment");
    else params.set("segment", s);
    setParams(params);
  };

  return (
    <div className="min-h-screen">
      {/* header banner */}
      <div className="bg-[#2C221E] py-12 text-center text-cream">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">Departures Board</span>
        <h1 className="mt-2 font-serif text-4xl text-[#F4E7C5] sm:text-5xl">
          {segment !== "All" ? `${segment} Collection` : category === "All" ? "The Full Collection" : category}
        </h1>
        <p className="mt-2 text-sm text-stone-400">Every piece flies first-class in a golden passport box.</p>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* segment toggle */}
        <div className="mb-6 flex justify-center gap-2">
          {SEGMENTS.map((s) => (
            <button
              key={s}
              data-testid={`filter-segment-${s.toLowerCase()}`}
              onClick={() => setSegment(s)}
              className={`rounded-full px-5 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                segment === s
                  ? "gold-gradient text-stone-900"
                  : "border border-[#E7DFD3] bg-white text-stone-600 hover:border-amber-500"
              }`}
            >
              {s === "All" ? "All Finishes" : s}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-category-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  category === c
                    ? "border-amber-600 bg-stone-900 text-cream"
                    : "border-[#E7DFD3] bg-white text-stone-700 hover:border-amber-500"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1 lg:w-56">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                data-testid="shop-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search treasures..."
                className="w-full rounded-full border border-[#E7DFD3] bg-white py-2 pl-9 pr-4 text-sm outline-none focus:border-amber-500"
              />
            </div>
            <div className="relative flex items-center gap-2 rounded-full border border-[#E7DFD3] bg-white px-3 py-2">
              <SlidersHorizontal size={15} className="text-stone-400" />
              <select
                data-testid="shop-sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent text-sm text-stone-700 outline-none"
              >
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <FlightPathDivider className="my-6" />

        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse rounded-xl bg-[#EAE2D3]" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center">
            <p className="font-serif text-2xl text-stone-700">No treasures on this flight yet.</p>
            <p className="mt-2 text-sm text-stone-500">Try another category or search term.</p>
          </div>
        ) : (
          <>
            <p data-testid="products-count" className="mb-4 font-mono text-[11px] uppercase tracking-widest text-stone-500">
              {products.length} pieces boarding
            </p>
            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
